import type { QuoteDraftItem } from '@/modules/products/types'
import { getMockCatalog } from '@/modules/search/utils/mock-catalog'
import { localAssistantProvider } from '../providers'
import { aiHistoryService } from '../history'
import { aiConfigRepository } from '../repositories'
import type {
  AIConfig,
  AIHistoryEntry,
  AIInsightBundle,
  CommercialObservationsResult,
  QuoteAnalysisInput,
  QuoteAnalysisItemInput,
  QuoteAnalysisResult,
  TechnicalDescriptionResult,
} from '../types'

/**
 * AIService — orchestration facade; never mutates quotes automatically.
 */
export class AIService {
  getConfig(): AIConfig {
    const config = aiConfigRepository.get()
    localAssistantProvider.setConfig(config)
    return config
  }

  saveConfig(config: AIConfig): AIConfig {
    const saved = aiConfigRepository.save(config)
    localAssistantProvider.setConfig(saved)
    return saved
  }

  listHistory(): AIHistoryEntry[] {
    return aiHistoryService.list()
  }

  draftToQuoteInput(items: QuoteDraftItem[], targetMargin = 20): QuoteAnalysisInput {
    return {
      quoteId: 'draft',
      targetMargin,
      items: items.map((item) => draftItemToAnalysis(item)),
    }
  }

  sampleQuoteInput(): QuoteAnalysisInput {
    const catalog = getMockCatalog().slice(0, 4)
    return {
      quoteId: 'sample',
      targetMargin: 20,
      items: catalog.map((p, index) => {
        const purchase = p.purchasePrice
        const sale = round2(p.pixPrice * 1.12)
        const profit = round2(sale - purchase)
        const margin = sale > 0 ? round2((profit / sale) * 100) : 0
        return {
          id: `sample-${p.id}`,
          description: p.description,
          quantity: 1 + (index % 2),
          purchasePrice: purchase,
          salePrice: index === 0 ? round2(purchase * 0.95) : sale,
          margin: index === 0 ? -5 : margin,
          profit: index === 0 ? round2(purchase * -0.05) : profit,
          stock: index === 2 ? 0 : p.stock,
          supplier: p.supplier,
          marketMinPrice: round2(p.pixPrice * 0.9),
          marketMaxPrice: round2(p.pixPrice * 1.35),
        }
      }),
    }
  }

  async analyzeQuote(input: QuoteAnalysisInput) {
    this.syncProvider()
    const response = await localAssistantProvider.analyzeQuote(input)
    aiHistoryService.record('analyze_quote', input, response)
    return response
  }

  async generateInsights(input: QuoteAnalysisInput) {
    this.syncProvider()
    const response = await localAssistantProvider.generateInsights(input)
    aiHistoryService.record('generate_insights', input, response)
    return response
  }

  async generateDescription(input: QuoteAnalysisInput) {
    this.syncProvider()
    const response = await localAssistantProvider.generateDescription(input)
    aiHistoryService.record('generate_description', input, response)
    return response
  }

  async generateObservations(input: QuoteAnalysisInput) {
    this.syncProvider()
    const response = await localAssistantProvider.generateObservations!(input)
    aiHistoryService.record('generate_observations', input, response)
    return response
  }

  async recommendPrice(input: {
    description: string
    purchasePrice: number
    currentSalePrice?: number
  }) {
    this.syncProvider()
    const response = await localAssistantProvider.recommendPrice(input)
    aiHistoryService.record('recommend_price', input, response)
    return response
  }

  async recommendSupplier(product: string) {
    this.syncProvider()
    const catalog = getMockCatalog()
      .filter((p) => p.description.toLowerCase().includes(product.toLowerCase().slice(0, 12)))
      .slice(0, 8)
    const candidates =
      catalog.length > 0
        ? catalog.map((p) => ({
            supplier: p.supplier,
            price: p.pixPrice,
            deliveryDays: p.estimatedDelivery,
            reliability: 70 + (p.supplier.length % 20),
          }))
        : getMockCatalog().slice(0, 6).map((p) => ({
            supplier: p.supplier,
            price: p.pixPrice,
            deliveryDays: p.estimatedDelivery,
            reliability: 75,
          }))
    const response = await localAssistantProvider.recommendSupplier({ product, candidates })
    aiHistoryService.record('recommend_supplier', { product, candidates }, response)
    return response
  }

  private syncProvider() {
    localAssistantProvider.setConfig(aiConfigRepository.get())
  }
}

function draftItemToAnalysis(item: QuoteDraftItem): QuoteAnalysisItemInput {
  const purchase = round2(item.valorManual * 0.82)
  const sale = item.valorManual
  const profit = round2(sale - purchase)
  const margin = sale > 0 ? round2((profit / sale) * 100) : 0
  const supplierMatch = item.descricao.match(/·\s*([a-z0-9_-]+)$/i)
  return {
    id: item.productId,
    description: item.descricao,
    quantity: item.quantity,
    purchasePrice: purchase,
    salePrice: sale,
    margin,
    profit,
    stock: 10,
    supplier: supplierMatch?.[1],
    marketMinPrice: round2(sale * 0.88),
    marketMaxPrice: round2(sale * 1.28),
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const aiService = new AIService()

export type {
  QuoteAnalysisResult,
  AIInsightBundle,
  TechnicalDescriptionResult,
  CommercialObservationsResult,
}
